import * as React from 'react';
import { cn } from '../lib/utils';

type CardProps = React.HTMLAttributes<HTMLDivElement>;

type CardHeaderProps = React.HTMLAttributes<HTMLDivElement>;

type CardContentProps = React.HTMLAttributes<HTMLDivElement>;

/**
 * Card container with glass effect (translucent border + blur).
 * Does not have a fixed size - adapts to content.
 * When h-full is passed, the container will stretch to fill available height.
 */
const Card = React.forwardRef<HTMLDivElement, CardProps>(({ className, children, ...props }, ref) => {
  // Check if h-full is in the className to apply it to the wrapper as well
  const hasFullHeight = className?.includes('h-full');

  return (
    <div className={cn('GlassContainer', hasFullHeight && 'h-full')}>
      <div ref={ref} className={cn('GlassContent', className)} {...props}>
        {children}
      </div>
    </div>
  );
});
Card.displayName = 'Card';

/**
 * Card header section - typically contains title and optional actions.
 */
const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('px-6 pt-4', className)} {...props} />
));
CardHeader.displayName = 'CardHeader';

/**
 * Card content section - main body of the card.
 */
const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
));
CardContent.displayName = 'CardContent';

export { Card, CardHeader, CardContent };
